import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface ProgramSetupProps {
  onProgramCreated?: () => void;
  onSetupComplete?: () => void;
}

export const ProgramSetup: React.FC<ProgramSetupProps> = ({ onProgramCreated, onSetupComplete }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    program_name: '',
    program_description: '',
    default_commission_rate: 20,
    commission_type: 'percentage',
    cookie_duration_days: 30,
    minimum_payout: 50,
    payment_terms: 'monthly',
    custom_payment_terms: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.program_name.trim()) {
      toast.error('Program name is required');
      return;
    }
    
    try {
      setLoading(true);

      await affiliateService.createProgram({
        program_name: String(formData.program_name).trim(),
        description: String(formData.program_description || ''),
        default_commission_rate: Number(formData.default_commission_rate),
        commission_type: (formData.commission_type as any) || 'percentage',
        cookie_duration_days: Number(formData.cookie_duration_days),
        minimum_payout: Number(formData.minimum_payout),
        payment_terms: String(formData.payment_terms || 'monthly'),
        custom_payment_terms: String(formData.custom_payment_terms || ''),
      });

      toast.success('Affiliate program created successfully!');
      if (onProgramCreated) {
        onProgramCreated();
      }
      if (onSetupComplete) {
        onSetupComplete();
      }
    } catch (error) {
      console.error('Error creating affiliate program:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create affiliate program');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Set Up Your Affiliate Program</h2>
        <p className="text-gray-600 mb-6">
          Create your own affiliate program to allow others to promote your products and earn commissions.
          Each affiliate will receive a unique referral link that they can share with their audience.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="program_name" className="block text-sm font-medium text-gray-700">
              Program Name*
            </label>
            <input
              type="text"
              id="program_name"
              name="program_name"
              value={formData.program_name}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="e.g. My Affiliate Program"
              required
            />
          </div>
          
          <div>
            <label htmlFor="program_description" className="block text-sm font-medium text-gray-700">
              Program Description
            </label>
            <textarea
              id="program_description"
              name="program_description"
              value={formData.program_description}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Describe your affiliate program and its benefits"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="commission_type" className="block text-sm font-medium text-gray-700">
                Commission Type
              </label>
              <select
                id="commission_type"
                name="commission_type"
                value={formData.commission_type}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="percentage">Percentage of Sale</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="default_commission_rate" className="block text-sm font-medium text-gray-700">
                Default Commission Rate {formData.commission_type === 'percentage' ? '(%)' : '(£)'}
              </label>
              <input
                type="number"
                id="default_commission_rate"
                name="default_commission_rate"
                value={formData.default_commission_rate}
                onChange={handleChange}
                min={0}
                step={formData.commission_type === 'percentage' ? 0.1 : 1}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="cookie_duration_days" className="block text-sm font-medium text-gray-700">
                Cookie Duration (Days)
              </label>
              <input
                type="number"
                id="cookie_duration_days"
                name="cookie_duration_days"
                value={formData.cookie_duration_days}
                onChange={handleChange}
                min={1}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                How long a referral will be credited to an affiliate after someone clicks their link
              </p>
            </div>
            
            <div>
              <label htmlFor="minimum_payout" className="block text-sm font-medium text-gray-700">
                Minimum Payout (£)
              </label>
              <input
                type="number"
                id="minimum_payout"
                name="minimum_payout"
                value={formData.minimum_payout}
                onChange={handleChange}
                min={0}
                step={0.01}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum amount an affiliate must earn before receiving a payout
              </p>
            </div>
          </div>
          
          <div>
            <label htmlFor="payment_terms" className="block text-sm font-medium text-gray-700">
              Payment Terms
            </label>
            <select
              id="payment_terms"
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          
          {formData.payment_terms === 'custom' && (
            <div>
              <label htmlFor="custom_payment_terms" className="block text-sm font-medium text-gray-700">
                Custom Payment Terms
              </label>
              <textarea
                id="custom_payment_terms"
                name="custom_payment_terms"
                value={formData.custom_payment_terms}
                onChange={handleChange}
                rows={2}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Describe your custom payment terms"
              />
            </div>
          )}
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : 'Create Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
